import { useSessionManagementServiceV1 } from "@rw1/api-services";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useParams } from "react-router-dom";
import {
  toApiExternalMetricFilter,
  toCumulativeMetricSeries,
} from "../../../../domain/mappers/metricsMappers";
import type { ExternalMetricQuery } from "../../../../domain/models/metrics";
import { GC_TIME_MS, STALE_TIME_MS } from "../../../../hooks/constants";
import { normalizeMetricDateRange } from "../../utils/metricDateRange";
import {
  anchorActivityTrendRangeStart,
  buildActivityTrendChartData,
  toActivityTrendSeries,
  type ActivityTrendDataPoint,
} from "../../utils/trendChartDataUtils";
import type { ExternalMetricsParams } from "./externalMetricsTypes";

export const EXTERNAL_ACTIVITY_TREND_METRICS_QUERY_KEY =
  "externalActivityTrendMetrics";

interface UseExternalActivityTrendDataParams extends ExternalMetricsParams {
  enabled?: boolean;
}

export interface UseExternalActivityTrendDataResult {
  chartData: ActivityTrendDataPoint[];
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
}

export const useExternalActivityTrendData = ({
  startDate,
  endDate,
  dimension,
  licenseIds,
  minSessionDurationInMinutes,
  enabled = true,
}: UseExternalActivityTrendDataParams): UseExternalActivityTrendDataResult => {
  const sessionManagementService = useSessionManagementServiceV1();

  const { companyId } = useParams();

  const normalizedQuery = useMemo<ExternalMetricQuery>(
    () => ({
      ...normalizeMetricDateRange(startDate, endDate),
      dimension,
      licenseIds,
      minSessionDurationInMinutes,
    }),
    [startDate, endDate, dimension, licenseIds, minSessionDurationInMinutes],
  );

  const { data, error, isLoading, isFetching } = useQuery({
    queryKey: [
      EXTERNAL_ACTIVITY_TREND_METRICS_QUERY_KEY,
      companyId,
      normalizedQuery.startDate.toISOString(),
      normalizedQuery.endDate.toISOString(),
      normalizedQuery.dimension,
      normalizedQuery.licenseIds,
      normalizedQuery.minSessionDurationInMinutes,
    ],
    queryFn: async () => {
      if (!companyId) {
        throw new Error("Company ID is required");
      }

      const result =
        await sessionManagementService.getCumulativeExternalMetrics(
          companyId,
          toApiExternalMetricFilter(normalizedQuery),
        );

      return toActivityTrendSeries(toCumulativeMetricSeries(result));
    },
    enabled: !!companyId && licenseIds.length > 0 && enabled,
    staleTime: STALE_TIME_MS,
    gcTime: GC_TIME_MS,
    placeholderData: keepPreviousData,
    retry: 1,
  });

  const chartData = useMemo<ActivityTrendDataPoint[]>(
    () =>
      anchorActivityTrendRangeStart(
        buildActivityTrendChartData(
          normalizedQuery.startDate,
          normalizedQuery.endDate,
          normalizedQuery.dimension,
          data ?? {},
        ),
      ),
    [data, normalizedQuery],
  );

  return {
    chartData,
    isLoading,
    isFetching,
    error,
  };
};
